import type { OrganisationPersonUsageRow } from "../../api/organisationReporting";

/** A few plain findings about how a group's spend divides among its people. Shares are whole percentages. */
export interface PeopleInsights {
  total: number;
  largest: number;
  /** The biggest spender's name and their share of the group's spend. */
  topName: string;
  topShare: number;
  /** How many people make up the top fifth (at least one), and their share of the spend. */
  topFifthCount: number;
  topFifthShare: number;
  /** The typical person's spend, and the plain average for comparison. */
  median: number;
  average: number;
}

/**
 * Works out how concentrated a group's spend is: the biggest spender's share, the top fifth's share, and the median
 * against the average. People with no converted spend count as zero.
 *
 * @param people - Everyone with spend in the period, in any order.
 */
export function peopleInsights(people: OrganisationPersonUsageRow[]): PeopleInsights {
  const amounts = people.map((person) => person.totalSpend ?? 0).sort((a, b) => b - a);
  const total = amounts.reduce((sum, amount) => sum + amount, 0);
  const share = (amount: number): number => (total > 0 ? Math.round((amount / total) * 100) : 0);
  const topFifthCount = Math.max(1, Math.round(amounts.length / 5));
  const top = [...people].sort((a, b) => (b.totalSpend ?? 0) - (a.totalSpend ?? 0))[0];

  return {
    total,
    largest: amounts[0] ?? 0,
    topName: top?.displayName ?? "",
    topShare: share(amounts[0] ?? 0),
    topFifthCount,
    topFifthShare: share(amounts.slice(0, topFifthCount).reduce((sum, amount) => sum + amount, 0)),
    median: median(amounts),
    average: amounts.length > 0 ? total / amounts.length : 0,
  };
}

function median(sortedDescending: number[]): number {
  const count = sortedDescending.length;
  if (count === 0) {
    return 0;
  }
  const middle = Math.floor(count / 2);
  return count % 2 === 1 ? sortedDescending[middle] : (sortedDescending[middle - 1] + sortedDescending[middle]) / 2;
}
